import { previewCharge, useGiveConfig, useGiveFlow, type GiveConfig } from '@church/data'
import {
  cents,
  formatCents,
  FREQUENCY_LABELS,
  GIFT_FREQUENCIES,
  giveFormSchema,
  type GiveForm,
  type GiveFormInput,
} from '@church/domain'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { faArrowRight, faCreditCard, faHeart, faRotateLeft } from '@church/ui-icons'
import { Button, ErrorNote, Field, Icon, Input, Select, Spinner } from '../components/ui.tsx'

/**
 * The public Give page — no account needed, and embeddable on the church's
 * own website in an iframe.
 *
 * Card details are never typed into anything this app renders. In Stripe mode
 * the payment step is Stripe's own Payment Element, loaded from js.stripe.com,
 * so the number goes from the giver's browser to Stripe; we see a token, a
 * brand and four digits. In test mode the step is a button the in-process test
 * processor answers.
 */
export const Route = createFileRoute('/give/$slug')({
  component: GivePage,
})

function GivePage() {
  const { slug } = Route.useParams()
  const config = useGiveConfig(slug)

  return (
    <div className="min-h-screen bg-surface-base px-4 py-10">
      <div className="mx-auto max-w-lg space-y-6">
        {config.isPending && <Spinner />}
        <ErrorNote error={config.error} />
        {config.data && <Give slug={slug} config={config.data} />}
      </div>
    </div>
  )
}

function Give({ slug, config }: { slug: string; config: GiveConfig }) {
  const flow = useGiveFlow(slug)
  const form = useForm<GiveFormInput, unknown, GiveForm>({
    resolver: zodResolver(giveFormSchema),
    defaultValues: {
      amount: '',
      fundId: config.funds[0]?.id ?? '',
      coverFees: true,
      method: 'card',
      frequency: 'once',
      firstName: '',
      lastName: '',
      email: '',
    },
  })
  const amount = form.watch('amount')
  const coverFees = form.watch('coverFees') ?? false
  const method = form.watch('method') ?? 'card'
  const frequency = form.watch('frequency') ?? 'once'
  const charge = previewCharge(amount, coverFees, method)
  const errors = form.formState.errors

  return (
    <div className="space-y-6">
      <header className="text-center">
        <p className="text-sm text-content-muted">Give to</p>
        <h1 className="text-3xl font-bold text-content-default">{config.churchName}</h1>
        {config.mode === 'test' && (
          <p className="mt-2 inline-block rounded bg-warning-soft px-2 py-1 text-xs text-warning-content">
            Test mode — no money moves
          </p>
        )}
      </header>

      {flow.step === 'form' && (
        <form
          className="space-y-4 rounded-2xl border border-border-subtle bg-surface-raised p-6"
          onSubmit={form.handleSubmit((values) => flow.start.mutate(values))}
          noValidate
        >
          <Field label="Amount" error={errors.amount?.message}>
            <Input
              {...form.register('amount')}
              inputMode="decimal"
              placeholder="$50"
              className="text-2xl"
              autoFocus
            />
          </Field>
          <div className="flex flex-wrap gap-2">
            {['25', '50', '100', '250'].map((preset) => (
              <button
                key={preset}
                type="button"
                className="rounded-lg border border-border-default px-3 py-1.5 text-sm hover:bg-surface-sunken"
                onClick={() => form.setValue('amount', preset)}
              >
                ${preset}
              </button>
            ))}
          </div>
          <Field label="Fund" error={errors.fundId?.message}>
            <Select {...form.register('fundId')}>
              {config.funds.map((fund) => (
                <option key={fund.id} value={fund.id}>
                  {fund.name}
                </option>
              ))}
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="How often">
              <Select {...form.register('frequency')}>
                <option value="once">Just this once</option>
                {GIFT_FREQUENCIES.map((value) => (
                  <option key={value} value={value}>
                    {FREQUENCY_LABELS[value]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Pay by">
              <Select {...form.register('method')}>
                <option value="card">Card</option>
                <option value="ach">Bank account</option>
              </Select>
            </Field>
          </div>
          <label className="flex items-start gap-2 text-sm text-content-default">
            <input type="checkbox" {...form.register('coverFees')} className="mt-1" />
            <span>
              Cover the processing fee so all of my gift reaches {config.churchName}
              {charge !== null && coverFees && (
                <span className="block text-content-muted">
                  {formatCents(charge)} will be charged
                </span>
              )}
            </span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name" error={errors.firstName?.message}>
              <Input {...form.register('firstName')} autoComplete="given-name" />
            </Field>
            <Field label="Last name" error={errors.lastName?.message}>
              <Input {...form.register('lastName')} autoComplete="family-name" />
            </Field>
          </div>
          <Field label="Email (for your receipt)" error={errors.email?.message}>
            <Input {...form.register('email')} type="email" autoComplete="email" />
          </Field>
          <ErrorNote error={flow.start.error} />
          <Button type="submit" className="w-full py-3 text-base" disabled={flow.start.isPending}>
            {charge === null
              ? 'Continue'
              : `Continue — ${formatCents(charge)}${frequency === 'once' ? '' : ` ${FREQUENCY_LABELS[frequency].toLowerCase()}`}`}
            <Icon icon={faArrowRight} />
          </Button>
        </form>
      )}

      {flow.step === 'pay' && flow.intent && (
        <div className="space-y-4 rounded-2xl border border-border-subtle bg-surface-raised p-6">
          <p className="text-center text-lg font-semibold text-content-default">
            {formatCents(cents(flow.intent.chargeCents))}
          </p>
          {config.mode === 'test' ? (
            <div className="space-y-3">
              <p className="text-sm text-content-muted">
                The test processor stands in for the card form. Nothing is charged; the gift is
                recorded exactly as a real one would be, through a signed webhook.
              </p>
              <Button
                className="w-full py-3"
                icon={faCreditCard}
                onClick={() => flow.confirmTest.mutate()}
                disabled={flow.confirmTest.isPending}
              >
                Pay with the test card ending 4242
              </Button>
              <ErrorNote error={flow.confirmTest.error} />
            </div>
          ) : (
            <StripePayment
              publishableKey={config.publishableKey ?? ''}
              stripeAccount={config.stripeAccount}
              clientSecret={flow.intent.clientSecret}
              onPaid={flow.markPaid}
            />
          )}
        </div>
      )}

      {flow.step === 'done' && (
        <div className="space-y-4 rounded-2xl border border-border-subtle bg-surface-raised p-8 text-center">
          <p className="text-4xl" aria-hidden>
            🙏
          </p>
          <h2 className="text-2xl font-bold text-content-default">Thank you</h2>
          <p className="text-content-muted">
            Your gift of {flow.intent ? formatCents(cents(flow.intent.chargeCents)) : ''} to{' '}
            {config.churchName} has been received. A receipt is on its way to your inbox.
          </p>
          <Button variant="secondary" icon={faRotateLeft} onClick={flow.giveAgain}>
            Give again
          </Button>
        </div>
      )}
    </div>
  )
}

interface StripeLike {
  elements(options: { clientSecret: string }): {
    create(type: 'payment'): { mount(element: HTMLElement): void }
  }
  confirmPayment(options: {
    elements: unknown
    redirect: 'if_required'
  }): Promise<{ error?: { message?: string } }>
}

/**
 * Stripe's Payment Element. The card fields are an iframe served by Stripe,
 * so the number never touches this page's JavaScript, let alone our servers.
 */
function StripePayment({
  publishableKey,
  stripeAccount,
  clientSecret,
  onPaid,
}: {
  publishableKey: string
  stripeAccount: string | null
  clientSecret: string
  onPaid: () => void
}) {
  const mount = useRef<HTMLDivElement>(null)
  const [stripe, setStripe] = useState<{ client: StripeLike; elements: unknown } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const load = () => {
      const factory = (
        window as unknown as {
          Stripe?: (key: string, options?: { stripeAccount?: string }) => StripeLike
        }
      ).Stripe
      if (!factory || !mount.current) return
      const client = factory(publishableKey, stripeAccount ? { stripeAccount } : undefined)
      const elements = client.elements({ clientSecret })
      elements.create('payment').mount(mount.current)
      setStripe({ client, elements })
    }
    if ((window as unknown as { Stripe?: unknown }).Stripe) return load()
    const script = document.createElement('script')
    script.src = 'https://js.stripe.com/v3/'
    script.onload = load
    script.onerror = () =>
      setError('The payment form could not load. Check your connection and try again.')
    document.head.appendChild(script)
  }, [clientSecret, publishableKey, stripeAccount])

  return (
    <div className="space-y-3">
      <div ref={mount} />
      {error && <p className="text-sm text-danger-solid">{error}</p>}
      <Button
        className="w-full py-3"
        icon={faHeart}
        disabled={!stripe || busy}
        onClick={async () => {
          if (!stripe) return
          setBusy(true)
          const result = await stripe.client.confirmPayment({
            elements: stripe.elements,
            redirect: 'if_required',
          })
          setBusy(false)
          if (result.error) setError(result.error.message ?? 'The payment did not go through.')
          else onPaid()
        }}
      >
        Give now
      </Button>
    </div>
  )
}
